import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Eye, EyeOff } from 'lucide-react';
import * as api from '../services/api';

const LoginPopup = ({ isOpen, onClose, onLoginSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Attempting login with:', { email: formData.email });
      const response = await api.login(formData);
      console.log('Login response:', response);
      
      if (response.access_token) {
        localStorage.setItem('access_token', response.access_token);

        // Get user data
        const userData = await api.getCurrentUser();
        localStorage.setItem('user', JSON.stringify(userData));

        console.log('Login successful, calling onLoginSuccess');
        onLoginSuccess(userData);
        onClose();
      } else {
        setError(t('loginPopup.invalidServerResponse'));
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.message && error.message !== 'Token expiré ou invalide') {
        setError(error.message);
      } else if (error.status === 401) {
        setError(t('loginPopup.invalidCredentials'));
      } else {
        setError(t('loginPopup.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div className="w-full max-w-md relative" style={{ background: '#FCFBF5', borderRadius: '28px', padding: '32px', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center transition-colors"
          style={{ width: '36px', height: '36px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 style={{ margin: '0 0 8px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '26px', letterSpacing: '-0.02em' }}>{t('loginPopup.title')}</h2>
          <p style={{ margin: 0, fontSize: '14.5px', color: 'rgba(5,16,4,0.66)' }}>{t('loginPopup.subtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Email */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{t('loginPopup.email')}</span>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '12px 18px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FFFFFF', color: '#051004', outline: 'none' }}
              placeholder="votre@email.com"
            />
          </label>

          {/* Password */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{t('loginPopup.password')}</span>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                style={{ width: '100%', border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '12px 44px 12px 18px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FFFFFF', color: '#051004', outline: 'none' }}
                placeholder={t('loginPopup.passwordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>

          {/* Error message */}
          {error && (
            <div className="form-message form-message--error text-center text-sm">
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              border: 'none', cursor: loading ? 'not-allowed' : 'pointer', background: '#054625', color: '#FBF9D9',
              fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15.5px', padding: '14px 20px', borderRadius: '999px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {t('loginPopup.loggingIn')}
              </>
            ) : (
              t('loginPopup.submit')
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="text-center" style={{ marginTop: '20px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
            {t('loginPopup.noAccountYet')}{' '}
            <button
              onClick={() => {
                onClose();
                // You can add navigation to register page here
                window.location.href = '/register';
              }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#EF4C23', fontWeight: 600, padding: 0 }}
            >
              {t('loginPopup.createAccount')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPopup;
